// Wage Order PDF generators (frontend)
// Looks for templates at /forms/wage_order.pdf and /forms/wage_order_motion.pdf
// Falls back to simple auto‑layout pages if templates are unavailable.
import { PDFDocument } from "pdf-lib";

const API_URL2 = process.env.REACT_APP_BACKEND_URL;



function downloadBlob(blob, filename) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  requestAnimationFrame(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  });
}

async function tryFetchTemplateBytes(url) {
  try {
    const resp = await fetch(url, { cache: "no-store" });
    if (!resp.ok) return null;
    return new Uint8Array(await resp.arrayBuffer());
  } catch {
    return null;
  }
}

async function optimizePdfBlob(blob) {
  const formData = new FormData();
  formData.append('file', blob, 'draft.pdf');
  try {
    const token = localStorage.getItem("accessToken");
    const res = await fetch(`${API_URL2}/optimize-pdf/`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    return res.ok ? await res.blob() : blob;
  } catch (err) {
    console.error('Optimization failed, returning original blob:', err);
    return blob;
  }
}

function periodLabel(period, customText) {
  switch (period) {
    case "weekly": return "week";
    case "biweekly": return "2 Weeks";
    case "semimonthly": return "Pay Period";
    case "monthly": return "Month";
    case "custom": return customText || "Custom";
    default: return String(period || "");
  }
}

function maskSSN(ssn) {
  if (!ssn) return "xxx-xx-";
  const d = String(ssn).replace(/[^0-9]/g, "");
  if (d.length < 4) return "" + d;
  return d.slice(-4);
}

function safeLine(...parts) { return parts.filter(Boolean).join(" "); }

function safeFilenamePart(value) {
  return String(value || "wage-order")
    .trim()
    .replace(/[^a-z0-9._-]+/gi, "_")
    .replace(/^_+|_+$/g, "") || "wage-order";
}

// --- ORDER (Proposed Order for Deductions) ---
async function generateOrderPDF({ caseData, debtor, employer, amount, period, customPeriodText }) {
  const templateBytes = await tryFetchTemplateBytes("/forms/wage_order.pdf");
  if (!templateBytes) throw new Error("Wage Order form template not found at /forms/wage_order.pdf");

  const caseNumber = caseData.case_number || ""; // Not written unless your form has a field for it
  const debtorFull = safeLine(debtor.first_name, debtor.second_name, debtor.last_name);
  const debtorSSN = safeLine(debtor.ssn);
  const amountStr = `${Number(amount || 0).toFixed(2)}`;
  const periodStr = periodLabel(period, customPeriodText);

  // Employer block must be full block in a single field
  const employerName = employer?.employer_name || employer?.employer || "";
  const empAddr1 = employer?.street1 || employer?.employer_street || "";
  const empAddr2 = employer?.street2 || employer?.employer_street2 || "";
  const empAddr3 = "Attn: Payroll"
  const empCityLine = safeLine(
    employer?.city && employer?.state ? `${employer.city}, ${employer.state}` : employer?.city || employer?.state || "",
    employer?.zip || employer?.employer_zip || ""
  );
  const employerBlock = [employerName, empAddr3,empAddr1, empAddr2, empCityLine].filter(Boolean).join("\n");

  const today = new Date();
  const dateStr = today.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const pdfDoc = await PDFDocument.load(templateBytes);
  const form = pdfDoc.getForm();

  const setIf = (name, value) => {
    try {
      const f = form.getTextField(name);
      if (typeof value === 'string') f.setText(value);
    } catch (_) {
      // Field not present — ignore silently
    }
  };

  setIf('Debtor', debtorFull);
  setIf('date', dateStr);
  setIf('payment', amountStr);
  setIf('period', periodStr);
  setIf('employer', employerBlock);
  setIf('case_number', caseNumber);
  setIf('ssn', maskSSN(debtorSSN));
  // If your PDF has a hidden/extra Case Number field, enable by name here, e.g.: setIf('case_number', caseNumber)

  form.flatten();

  const bytes = await pdfDoc.save();
  const filename = `WageOrder_${safeFilenamePart(caseNumber || debtorFull)}.pdf`;
  const blob = new Blob([bytes], { type: 'application/pdf' });
  return { blob: await optimizePdfBlob(blob), filename };
}

// --- MOTION (Motion for Wage Order) ---
async function generateMotionPDF({ caseData, debtor }) {
  const templateBytes = await tryFetchTemplateBytes("/forms/wage_order_motion.pdf");
  if (!templateBytes) throw new Error("Wage Order Motion template not found at /forms/wage_order_motion.pdf");

  const debtorFull = safeLine(debtor.first_name, debtor.second_name, debtor.last_name);
  const dateStr = (new Date()).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const caseNumber = caseData.case_number || ""; // Not written unless your form has a field for it
  const debtorSSN = safeLine(debtor.ssn);
  const pdfDoc = await PDFDocument.load(templateBytes);
  const form = pdfDoc.getForm();

  const setIf = (name, value) => {
    try {
      const f = form.getTextField(name);
      if (typeof value === 'string') f.setText(value);
    } catch (_) {
      // Field not present — ignore silently
    }
  };

  setIf('Debtor', debtorFull);
  setIf('date', dateStr);
  setIf('case_number', caseNumber);
  setIf('ssn', maskSSN(debtorSSN));

  // If your Motion has other fields, map them here by exact field name.

  form.flatten();

  const bytes = await pdfDoc.save();
  const filename = `WageOrder_Motion_${safeFilenamePart(caseData.case_number || debtorFull)}.pdf`;
  const blob = new Blob([bytes], { type: 'application/pdf' });
  return { blob: await optimizePdfBlob(blob), filename };
}

export async function buildWageOrderPDFs({ caseData, debtor, employer, amount, period, customPeriodText = "" }) {
  if (!caseData) throw new Error("caseData is required");
  if (!debtor) throw new Error("debtor is required");
  if (!employer) throw new Error("employer is required");

  const [motion, order] = await Promise.all([
    generateMotionPDF({ caseData, debtor }),
    generateOrderPDF({ caseData, debtor, employer, amount, period, customPeriodText }),
  ]);
  return { motion, order };
}

// PUBLIC API: generate both Motion and Order and download them locally.
export async function generateWageOrderPDF(options) {
  const documents = await buildWageOrderPDFs(options);
  downloadBlob(documents.motion.blob, documents.motion.filename);
  downloadBlob(documents.order.blob, documents.order.filename);
  return documents;
}
